import { AnyActorRef } from "xstate";
import { DateTime } from "luxon";

import { ReservationFormMainContext } from "../machines/reservationFormMain.machine";
import { TripSegmentValues } from "../schema/reservationForm.schema";
import { emptyStringIfOnlyNanpCallingCode } from "@/services/phoneUtils";
import { TripArrayFieldContext } from "../machines/createTripArrayFieldMachine";
import { WillCallContext } from "../machines/willCall.machine";
import { StandingOrderContext } from "../machines/standingOrder.machine";
import { LevelOfCareContext } from "../machines/levelOfCare.machine";


function readTripArrayField(ref: AnyActorRef | null): unknown[] {
  if (!ref) return [''];
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

function readWillCallArray(ref: AnyActorRef | null): boolean[] {
  if (!ref) return [false];
  const ctx = ref.getSnapshot().context as WillCallContext;
  return ctx.values.length ? ctx.values : [false];
}

function readStandingOrderArray(ref: AnyActorRef | null): boolean[] {
  if (!ref) return [false];
  const ctx = ref.getSnapshot().context as StandingOrderContext;
  return ctx.values.length ? ctx.values : [false];
}

function readLevelOfCareStringArray(
  ref: AnyActorRef | null,
  key:
    | 'attributeLevel1Values'
    | 'attributeLevel2Values'
    | 'attributeLevel3Values'
    | 'attributeLevel4Values'
    | 'attributeLevel5Values'
    | 'attributeLevel6Values',
): string[] {
  if (!ref) return [''];
  const ctx = ref.getSnapshot().context as LevelOfCareContext;
  const arr = ctx[key];
  return arr.length ? arr : [''];
}

function getTripsFromMachines(
  context: ReservationFormMainContext,
): TripSegmentValues[] {
  const reasonForTrips = readTripArrayField(
    context.reasonForTripRef,
  ) as string[];
  const appointmentDateTimeIso = readTripArrayField(
    context.appointmentDateTimeRef,
  ) as string[];
  const appointmentDates = readTripArrayField(
    context.appointmentDateRef,
  ) as string[];
  const pickupDateTimeIso = readTripArrayField(
    context.pickupDateTimeRef,
  ) as string[];
  const pickupDates = readTripArrayField(context.pickupDateRef) as string[];
  const pickupTimeZones = readTripArrayField(
    context.pickupTimeZoneRef,
  ) as string[];
  const dropoffTimeZones = readTripArrayField(
    context.dropoffTimeZoneRef,
  ) as string[];
  const pickupAddresses = readTripArrayField(context.pickupAddressRef);
  const dropoffAddresses = readTripArrayField(context.dropoffAddressRef);
  const pickupPhones = readTripArrayField(context.pickupPhoneRef) as string[];
  const dropoffPhones = readTripArrayField(context.dropoffPhoneRef) as string[];
  const pickupNotes = readTripArrayField(context.pickupNotesRef) as string[];
  const dropoffNotes = readTripArrayField(context.dropoffNotesRef) as string[];
  const willCalls = readWillCallArray(context.willCallRef);
  const standingOrders = readStandingOrderArray(context.standingOrderRef);
  const passengerMobilities = readTripArrayField(
    context.passengerMobilityRef,
  ) as string[];
  const passengerAssistances = readTripArrayField(
    context.passengerAssistanceRef,
  ) as string[];
  const attributeLevel1Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel1Values',
  );
  const attributeLevel2Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel2Values',
  );
  const attributeLevel3Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel3Values',
  );
  const attributeLevel4Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel4Values',
  );
  const attributeLevel5Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel5Values',
  );
  const attributeLevel6Values = readLevelOfCareStringArray(
    context.levelOfCareRef,
    'attributeLevel6Values',
  );
  const len = Math.max(
    reasonForTrips.length,
    appointmentDateTimeIso.length,
    appointmentDates.length,
    pickupDateTimeIso.length,
    pickupDates.length,
    pickupAddresses.length,
    dropoffAddresses.length,
    pickupPhones.length,
    dropoffPhones.length,
    pickupNotes.length,
    dropoffNotes.length,
    willCalls.length,
    standingOrders.length,
    passengerMobilities.length,
    passengerAssistances.length,
    attributeLevel1Values.length,
    attributeLevel2Values.length,
    attributeLevel3Values.length,
    attributeLevel4Values.length,
    attributeLevel5Values.length,
    attributeLevel6Values.length,
    1,
  );
  const trips: TripSegmentValues[] = [];
  for (let i = 0; i < len; i++) {
    const iso = appointmentDateTimeIso[i] ?? '';
    const parsed = iso ? DateTime.fromISO(iso) : null;
    const pickupIso = pickupDateTimeIso[i] ?? '';
    const pickupParsed = pickupIso ? DateTime.fromISO(pickupIso) : null;
    const isWillCall = willCalls[i] ?? false;
    const appointmentDateTime = isWillCall
      ? (null as unknown as TripSegmentValues['appointmentDateTime'])
      : parsed?.isValid === true
        ? parsed
        : (null as unknown as TripSegmentValues['appointmentDateTime']);
    const pickupDateTime = isWillCall
      ? (null as unknown as TripSegmentValues['pickupDateTime'])
      : pickupParsed?.isValid === true
        ? pickupParsed
        : (null as unknown as TripSegmentValues['pickupDateTime']);
    trips.push({
      reasonForTrip: reasonForTrips[i] ?? '',
      appointmentDate: appointmentDates[i] ?? '',
      isWillCall,
      appointmentDateTime,
      pickupDate: pickupDates[i] ?? '',
      pickupDateTime,
      pickupTimeZone: pickupTimeZones[i] ?? '',
      dropoffTimeZone: dropoffTimeZones[i] ?? '',
      pickupAddress: (pickupAddresses[i] ??
        '') as TripSegmentValues['pickupAddress'],
      dropoffAddress: (dropoffAddresses[i] ??
        '') as TripSegmentValues['dropoffAddress'],
      pickupPhone: emptyStringIfOnlyNanpCallingCode(
        String(pickupPhones[i] ?? ''),
      ),
      dropoffPhone: emptyStringIfOnlyNanpCallingCode(
        String(dropoffPhones[i] ?? ''),
      ),
      pickupPhoneExtension: '',
      dropoffPhoneExtension: '',
      pickupNotes: pickupNotes[i] ?? '',
      dropoffNotes: dropoffNotes[i] ?? '',
      roundTripWillCall: true,
      standingOrder: standingOrders[i] ?? false,
      passengerMobility: passengerMobilities[i] ?? '',
      passengerAssistance: passengerAssistances[i] ?? '',
      AttributeLevel1: attributeLevel1Values[i] ?? '',
      AttributeLevel2: attributeLevel2Values[i] ?? '',
      AttributeLevel3: attributeLevel3Values[i] ?? '',
      AttributeLevel4: attributeLevel4Values[i] ?? '',
      AttributeLevel5: attributeLevel5Values[i] ?? '',
      AttributeLevel6: attributeLevel6Values[i] ?? '',
      MobilityAttribute: passengerAssistances[i] ?? '',
      height: '',
      weight: '',
    });
  }
  return trips;
}

export { getTripsFromMachines };
